import { App } from 'App';
import { getAnalyticsCookies } from 'utils/getAnalyticsCookies';
import { PostRegistrationPayload } from 'api/typegen/registration/post';
import { R } from 'config/app';
import { RegisterFormValues } from 'types/ui';
import { useDeviceDetection } from 'internalLibrary';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ValidationError,
  ValidationFieldError,
} from 'api/errors/ValidationError';
import Cookies from 'js-cookie';
import getConsentsApiCall from 'api/calls/getConsentsApiCall';
import useAuth from 'ui/hooks/useAuth';
import useCreateRegistrationMutation from 'ui/hooks/useCreateRegistrationMutation';
import useHandleExistingLoginNavigation from 'ui/hooks/useHandleExistingLogin';
import useIoBlackbox from 'ui/hooks/useIoBlackbox';
import useRedirectToDashboard from 'ui/hooks/useRedirectToDashboard';
import useValidAMLExistsQuery from 'ui/hooks/useValidAMLExistsQuery';

interface UseRegisterUser {
  handleRegister: (values: RegisterFormValues) => Promise<void>;
  isSubmitting: boolean;
  bankAccountError: string;
  emailError: string;
  mobilePhoneError: string;
  personalIdError: string;
}

const useRegisterUser = (): UseRegisterUser => {
  const ioBlackBox = useIoBlackbox();
  const { refetch: fetchValidAMLExists } = useValidAMLExistsQuery();
  const navigate = useNavigate();
  const { login, loggingInStatus } = useAuth();
  const { isMobile } = useDeviceDetection();
  const consentsQuery = useQuery(['getConsents'], getConsentsApiCall, {
    staleTime: Infinity,
    select(data) {
      return data?.entries || [];
    },
  });
  const { isRedirectingToDashboard } = useRedirectToDashboard();
  const createRegistrationMutation = useCreateRegistrationMutation();
  const { mutateAsync: createRegistration, error: createRegistrationError } =
    createRegistrationMutation;
  const [loginTriedAndFailed, setLoginTriedAndFailed] = useState(false);
  const errors =
    createRegistrationError &&
    createRegistrationError instanceof ValidationError
      ? createRegistrationError.fieldErrors
      : [];
  const { handleLoginNavigation } = useHandleExistingLoginNavigation();
  const bankAccountError = useBankAccountError(errors, loginTriedAndFailed);
  const emailError = useEmailError(errors, loginTriedAndFailed);
  const mobilePhoneError = useMobilePhoneError(errors, loginTriedAndFailed);
  const personalIdError = usePersonalIdError(errors, loginTriedAndFailed);

  const handleRegister = async (values: RegisterFormValues) => {
    let alreadyExisting = false;

    try {
      await createRegistration(
        registerFormToPostRegistration(
          values,
          isMobile,
          consentsQuery.data,
          ioBlackBox,
        ),
      );
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        return;
      }

      if (
        !e.fieldErrors.find(
          (fe) => fe.messageTemplate === 'already_exists_in_db',
        )
      ) {
        return;
      }

      alreadyExisting = true;
    }

    try {
      const data = await login(values.email, values.password);

      // 2faOptionalComment: Remove changes related 2fa options when BE deployment is done on PROD [zaquiel]
      const isTwoFAEnabled = Boolean(data.verificationId);
      if (isTwoFAEnabled) return;

      if (alreadyExisting) {
        handleLoginNavigation();
        return;
      }
    } catch (e) {
      setLoginTriedAndFailed(true);
    }

    const { data } = await fetchValidAMLExists();

    if (!data?.validAmlExists) {
      navigate(R.FirstLoanQuestionnaire);
      return;
    }
    navigate(R.FirstLoanIdentificationMethod);
  };

  return {
    handleRegister,
    isSubmitting:
      loggingInStatus === 'loading' ||
      createRegistrationMutation.status === 'loading' ||
      createRegistrationMutation.status === 'success' ||
      isRedirectingToDashboard,
    bankAccountError,
    emailError,
    mobilePhoneError,
    personalIdError,
  };
};

export default useRegisterUser;

type Consents = {
  name: string;
  version: string;
};

const registerFormToPostRegistration = (
  values: RegisterFormValues,
  isMobile: boolean,
  consents: Consents[],
  ioBlackBox?: string,
): PostRegistrationPayload => {
  const affiliateProvider = Cookies.get('affiliate_provider');
  const affiliateToken = Cookies.get('affiliate_token');
  const affiliatePartner = Cookies.get('affiliate_partner');

  return {
    analyticsCookies: getAnalyticsCookies(),
    personalId:
      values.prePersonalId.trim() + '-' + values.postPersonalId.trim(),
    mobilePhone: values.phone.trim(),
    email: values.email.trim(),
    password: values.password,
    passwordRepeat: values.password,
    source: isMobile ? 'MOBILE' : 'DESKTOP',
    acceptAgreement: values.agreement1,
    acceptProfiling: values.agreement1,
    acceptNews: values.agreement2,
    acceptCommunication: values.agreement2,
    agreeGetNews: values.agreement2,
    ioBlackBox,
    productNumber: App.config.productNumber.trim(),
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    monthlyIncomeAmount: Number(values.monthlyIncome),
    monthlyOtherLoansAmount: Number(values.monthlyOtherLoans),
    monthlyHousingExpensesAmount: Number(values.monthlyHousingCosts),
    monthlyOtherLivingExpensesAmount: Number(values.monthlyOtherLivingCosts),
    adultsInFamily: Number(values.numberOfAdultsInHouse),
    dependants: Number(values.numberOfChildrenInHouse),
    bankAccountNumber:
      values.bankAccountClearingNumber.trim() +
      '-' +
      values.bankAccountNumber.trim(),
    incomeEnteredAfterTax: Number(values.monthlyIncome),
    consents: !values.agreement2
      ? []
      : consents.map((consent) => ({
          name: consent.name,
          version: consent.version,
          accepted: true,
        })),
    affiliateInfo:
      affiliateProvider || affiliatePartner || affiliateToken
        ? {
            provider: affiliateProvider,
            token: affiliateToken,
            partner: affiliatePartner,
          }
        : undefined,
  };
};

const useBankAccountError = (
  errors: ValidationFieldError[],
  loginTriedAndFailed: boolean,
): string => {
  const { t } = useTranslation();
  const messageTemplate =
    errors.find((err) => err.property === 'bankAccountNumber')
      ?.messageTemplate || '';

  if (messageTemplate === 'already_exists_in_db' && loginTriedAndFailed)
    return t('bankAccountNumberAlreadyInUse');
  else if (messageTemplate === 'incorrect_format') return t('incorrectFormat');
  else return '';
};

const useEmailError = (
  errors: ValidationFieldError[],
  loginTriedAndFailed: boolean,
): string => {
  const { t } = useTranslation();
  const messageTemplate =
    errors.find((err) => err.property === 'email')?.messageTemplate || '';

  if (messageTemplate === 'already_exists_in_db' && loginTriedAndFailed)
    return t('emailAlreadyInUse');
  else if (messageTemplate === 'incorrect_format') return t('incorrectFormat');
  else return '';
};

const useMobilePhoneError = (
  errors: ValidationFieldError[],
  loginTriedAndFailed: boolean,
): string => {
  const { t } = useTranslation();
  const messageTemplate =
    errors.find((err) => err.property === 'mobilePhone')?.messageTemplate || '';

  if (messageTemplate === 'already_exists_in_db' && loginTriedAndFailed)
    return t('phoneAlreadyInUse');
  else if (messageTemplate === 'invalid_phone_number')
    return t('phoneIncorrectFormat');
  else if (messageTemplate === 'incorrect_format') return t('incorrectFormat');
  else return '';
};

const usePersonalIdError = (
  errors: ValidationFieldError[],
  loginTriedAndFailed: boolean,
): string => {
  const { t } = useTranslation();
  const messageTemplate =
    errors.find((err) => err.property === 'personalId')?.messageTemplate || '';

  if (messageTemplate === 'already_exists_in_db' && loginTriedAndFailed)
    return t('personalIdAlreadyInUse');
  else if (messageTemplate === 'incorrect_format') return t('incorrectFormat');
  else return '';
};
